export type AvailabilitySlot = { iso: string; label: string };
export type AvailabilityDay = {
  key: string;
  dayLabel: string;
  dateLabel: string;
  slots: AvailabilitySlot[];
};

const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: 'long' });
const dateFmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

/**
 * Groups ISO consult timeslots by local calendar day and formats each slot as a
 * `start - end` range (end derived from the consult `durationMinutes`). Day +
 * intra-day slot order follow chronological order — we sort here rather than
 * trusting the API's order, so an out-of-order response (cache layer, parallel
 * merge, edge reorder) can't make the rail start mid-week or jumble slots.
 */
export function groupTimeslots(timeslots: string[], durationMinutes: number): AvailabilityDay[] {
  const days: AvailabilityDay[] = [];
  const byKey = new Map<string, AvailabilityDay>();

  const sorted = timeslots
    .map((iso) => ({ iso, time: new Date(iso).getTime() }))
    .filter(({ time }) => !Number.isNaN(time))
    .sort((a, b) => a.time - b.time);

  for (const { iso } of sorted) {
    const start = new Date(iso);
    const key = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;

    let day = byKey.get(key);
    if (!day) {
      day = { key, dayLabel: dayFmt.format(start), dateLabel: dateFmt.format(start), slots: [] };
      byKey.set(key, day);
      days.push(day);
    }

    const end = new Date(start.getTime() + durationMinutes * 60_000);
    const label =
      durationMinutes > 0
        ? `${timeFmt.format(start)} - ${timeFmt.format(end)}`
        : timeFmt.format(start);
    day.slots.push({ iso, label });
  }

  return days;
}
